"use client"

import { Image, Text } from "@/elements"
import { FragmentType, graphql, useFragment } from "@/gql"
import { JobApplicationProjectPreviewFragmentDoc } from "@/gql/graphql"
import { BriefcaseIcon, CameraIcon } from "@/icons/solid"
import { RolePreview } from "./RolePreview"
import { ProjectSideDrawer } from "../ProjectSideDrawer"
import { useState } from "react"
import type { NovaSession } from "@/types"
import clsx from "clsx"

graphql(`
  fragment JobApplicationProjectPreview on CreativeWorkPortfolioProject {
    id
    title
    description
    thumbnail645w: thumbnailUrl(width: 645)
    thumbnailFocalPointX
    thumbnailFocalPointY
    category {
      name
      id
    }
    ...ApplicationRolePreview
  }
`)

interface Props {
  projectData: FragmentType<typeof JobApplicationProjectPreviewFragmentDoc> | undefined | null
  positionId: string | undefined
  imageWidth?: string
  imageClassName?: string
  aspectRatio?: string
  onClick?: () => void
}

export const ProjectPreviewCard: React.FC<Props> = ({
  positionId,
  imageClassName = "h-[200px] ",
  imageWidth = "100%",
  aspectRatio = "1/1",
  onClick,

  ...props
}) => {
  const projectData = useFragment(JobApplicationProjectPreviewFragmentDoc, props.projectData)
  const xPosition = projectData?.thumbnailFocalPointX ?? 0.5
  const yPosition = projectData?.thumbnailFocalPointY ?? 0.5
  if (!projectData || !props.projectData) return null
  return (
    <>
      <div
        className={clsx(
          "relative flex h-auto shrink-0 flex-col overflow-hidden rounded-md",
          Boolean(onClick) ? "cursor-pointer" : "cursor-default"
        )}
        style={{
          maxWidth: imageWidth,
        }}
        onClick={onClick}>
        <div className={clsx("overflow-hidden", imageClassName)} style={{ width: imageWidth }}>
          <Image
            srcSetUrls={{
              sm: projectData.thumbnail645w || "",
            }}
            alt={projectData.title || ""}
            className="h-full object-cover"
            style={{
              width: imageWidth,
              aspectRatio: aspectRatio,
              objectPosition: `${xPosition * 100}% ${yPosition * 100}%`,
            }}
          />
        </div>

        <div className="flex h-auto min-h-[88px] flex-1 flex-col rounded-b-md border border-b border-l border-r border-bg-3 p-3">
          <Text className="mb-2 h-5" maxLines={1} variant="body2">
            {projectData.title}
          </Text>
          <div className="flex flex-col gap-y-[2px]">
            <RolePreview creativeWork={projectData} positionId={positionId} />
            {projectData.category ? (
              <div className="flex h-4 flex-row items-center gap-x-[6px]">
                <BriefcaseIcon width={12} height={12} className="shrink-0 fill-fg-2 stroke-fg-2" />
                <Text variant="caption" className="text-fg-2">
                  {projectData.category?.name}
                </Text>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </>
  )
}
